// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/components/ClientInfo.module.css';
import Config from '@app/features/app/config/Config';
import DeveloperMode from '@app/features/devtools/state/DeveloperMode';
import {formatClientBuildInfo, getClientInfo, getClientInfoSync} from '@app/features/platform/utils/clientInfo';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {formatShortRelativeTime} from '@app/vendor/date_utils/DateDuration';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
export const ClientInfo = observer(() => {
	const {t, i18n} = useLingui();
	const [clientInfo, setClientInfo] = useState(getClientInfoSync());
	useEffect(() => {
		let mounted = true;
		void getClientInfo().then((info) => {
			if (!mounted) return;
			setClientInfo(info);
		});
		return () => {
			mounted = false;
		};
	}, []);
	const buildShaShort = (Config.PUBLIC_BUILD_SHA ?? '').slice(0, 7);
	const buildNumber = Config.PUBLIC_BUILD_NUMBER;
	const desktopVersion = clientInfo.desktopVersion;
	const desktopChannel = clientInfo.desktopChannel;
	const browserName = clientInfo.browserName || 'Unknown';
	const browserVersion = clientInfo.browserVersion || '';
	const osName = clientInfo.osName || 'Unknown';
	const rawOsVersion = clientInfo.osVersion ?? '';
	const isDesktopApp = isDesktop();
	const osArchitecture = clientInfo.desktopArch ?? clientInfo.arch;
	const shouldShowOsVersion = Boolean(rawOsVersion) && (isDesktopApp || osName !== 'macOS');
	const osVersionForDisplay = shouldShowOsVersion ? rawOsVersion : undefined;
	const buildOsDescription = () => {
		const parts = [osName];
		if (osVersionForDisplay) {
			parts.push(osVersionForDisplay);
		}
		const archSuffix = osArchitecture ? ` (${osArchitecture})` : '';
		return `${parts.join(' ')}${archSuffix}`.trim();
	};
	const osDescription = buildOsDescription();
	const onClick = () => {
		const justUnlocked = DeveloperMode.registerBuildTap();
		if (justUnlocked) {
			ToastCommands.success(t`You are now a developer!`);
		}
		TextCopyCommands.copy(i18n, formatClientBuildInfo(clientInfo));
	};
	return (
		<Tooltip text={t`Click to copy`}>
			<FocusRing>
				<button type="button" onClick={onClick} className={styles.button}>
					<span>
						{Config.PUBLIC_RELEASE_CHANNEL}{' '}
						{buildNumber ? `build ${buildNumber} (${buildShaShort})` : `(${buildShaShort})`}
					</span>
					{desktopVersion && (
						<span>
							Desktop {desktopChannel ?? 'stable'} {desktopVersion}
						</span>
					)}
					{Config.PUBLIC_BUILD_TIMESTAMP && (
						<span>
							<Trans>Deployed</Trans> {formatShortRelativeTime(Config.PUBLIC_BUILD_TIMESTAMP * 1000)}
						</span>
					)}
					<span>
						{browserName} {browserVersion}
					</span>
					<span>{osDescription}</span>
				</button>
			</FocusRing>
		</Tooltip>
	);
});
